/**
 * Shared helpers for entity and single action generators
 */

import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships.js'
import { toTypeScriptType } from '@core-engine/schema/type-mappers/index.js'
import type { GeneratedFile, SchemaField } from '@core-engine/schema/types.js'
import {
  quotePropertyName,
  singularize,
  toCamelCase,
  toPascalCase
} from '@core-engine/utils/string.js'

// ============================================================================
// Authorship Metadata Helpers
// ============================================================================

export const AUTHORSHIP_FIELD_NAMES = ['createdBy', 'updatedBy'] as const

export function isAuthorshipFieldName(name: string): boolean {
  return (AUTHORSHIP_FIELD_NAMES as readonly string[]).includes(name)
}

export function createAuthorshipFields(): SchemaField[] {
  return AUTHORSHIP_FIELD_NAMES.map((name) => ({
    name,
    type: 'text' as const,
    required: false
  }))
}

export function buildAuthorshipUserType(): string {
  return `import type { AdminUserSummary } from '@admin/types'`
}

export function buildAuthorshipDataFields(): string {
  return `  createdByUser: AdminUserSummary | null
  updatedByUser: AdminUserSummary | null`
}

export function buildAuthorshipSelects(indent = '      '): string[] {
  return [
    `${indent}createdByUser: { id: createdByUser.id, name: createdByUser.name, email: createdByUser.email }`,
    `${indent}updatedByUser: { id: updatedByUser.id, name: updatedByUser.name, email: updatedByUser.email }`
  ]
}

export function buildAuthorshipJoins(tableVar: string): string {
  return `.leftJoin(createdByUser, eq(${tableVar}.createdBy, createdByUser.id)).leftJoin(updatedByUser, eq(${tableVar}.updatedBy, updatedByUser.id))`
}

export function buildAuthorshipAliasBlock(): string {
  return `const createdByUser = alias(user, 'created_by_user')
const updatedByUser = alias(user, 'updated_by_user')`
}

// ============================================================================
// Public API
// ============================================================================

export interface ActionsGeneratorResult {
  files: GeneratedFile[]
}

// ============================================================================
// Field Mapping Helpers
// ============================================================================

export function formatActionDate(value: unknown): string {
  if (typeof value !== 'string') return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC'
  })
}

export function generateFieldMapping(field: SchemaField, source = 'input'): string {
  if (field.type === 'list') return `${source}.${field.name} || []`
  if (field.type === 'select' && field.multiple) return `${source}.${field.name} ?? []`
  if (field.type === 'relationship') {
    if (field.multiple) return `${source}.${field.name} ?? []`
    return field.required
      ? `${source}.${field.name}`
      : `${source}.${field.name} && ${source}.${field.name} !== '' ? ${source}.${field.name} : null`
  }
  if (
    (field.type === 'date' || field.type === 'timestamp' || field.type === 'time') &&
    !field.required
  ) {
    return `${source}.${field.name} && ${source}.${field.name} !== '' ? ${source}.${field.name} : null`
  }
  if (!field.required && ['string', 'email', 'varchar', 'text', 'select'].includes(field.type)) {
    return `${source}.${field.name} && ${source}.${field.name} !== '' ? ${source}.${field.name} : null`
  }
  return `${source}.${field.name}`
}

export function getFieldType(field: SchemaField, mode: 'input' | 'output' = 'output'): string {
  return toTypeScriptType(field, mode)
}

export function getInputFieldType(field: SchemaField): string {
  const fieldType = getFieldType(field, 'input')
  if (!field.required && (field.type === 'number' || field.type === 'decimal')) {
    return `${fieldType} | null`
  }
  return fieldType
}

export function getReadFieldType(field: SchemaField): string {
  if (field.type === 'select') return 'SelectOptionValue[]'
  if (field.type === 'group' && field.fields && field.fields.length > 0) {
    const groupType = field.fields
      .map(
        (nestedField) => `${quotePropertyName(nestedField.name)}?: ${getReadFieldType(nestedField)}`
      )
      .join('; ')
    return `{ ${groupType} }`
  }
  if (field.type === 'list' && field.fields && field.fields.length > 0) {
    const nestedType = field.fields
      .flatMap((nestedField) => {
        const fields = [`${quotePropertyName(nestedField.name)}?: ${getReadFieldType(nestedField)}`]
        if (nestedField.hasIcon) {
          fields.push(`${quotePropertyName(`${nestedField.name}Icon`)}?: string`)
        }
        return fields
      })
      .join('; ')
    return `Array<{ ${nestedType} }>`
  }
  return getFieldType(field, 'output')
}

export interface ReadSelectField {
  field: SchemaField
  path: string[]
}

export function collectReadSelectFields(
  fields: SchemaField[],
  path: string[] = []
): ReadSelectField[] {
  const matches = fields.flatMap((field) => {
    const currentPath = [...path, field.name]
    const matches: ReadSelectField[] = field.type === 'select' ? [{ field, path: currentPath }] : []
    if (field.fields) {
      matches.push(...collectReadSelectFields(field.fields, currentPath))
    }
    if (field.tabs) {
      for (const tab of field.tabs) {
        matches.push(...collectReadSelectFields(tab.fields ?? [], currentPath))
        if (tab.slot?.main?.fields) {
          matches.push(...collectReadSelectFields(tab.slot.main.fields, currentPath))
        }
        if (tab.slot?.sidebar?.fields) {
          matches.push(...collectReadSelectFields(tab.slot.sidebar.fields, currentPath))
        }
      }
    }
    return matches
  })
  const seen = new Set<string>()
  return matches.filter((match) => {
    const key = match.path.join('.')
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export function buildReadSelectOptionType(selectFields: ReadSelectField[]): string | null {
  if (selectFields.length === 0) return null

  return `export interface SelectOptionValue {
  label: string
  value: string
}`
}

// ============================================================================
// Helper builders for relationship queries
// ============================================================================

function relationshipDataTypeName(field: SchemaField): string {
  if (isUserRelationshipTarget(field.relationship)) {
    return 'UserData'
  }

  const relationshipSingular = singularize(field.relationship ?? 'relationship')
  return `${toPascalCase(relationshipSingular)}Data`
}

export function buildRelationshipDataTypes(fields: SchemaField[]): string | null {
  const relationshipFields = fields.filter((field) => field.type === 'relationship')
  if (relationshipFields.length === 0) return null

  const typeDeclarations = new Map<string, string>()
  for (const field of relationshipFields) {
    const typeName = relationshipDataTypeName(field)
    if (typeDeclarations.has(typeName)) continue

    typeDeclarations.set(
      typeName,
      isUserRelationshipTarget(field.relationship)
        ? `export interface ${typeName} {
  id: string
  name?: string | null
  email?: string | null
}`
        : `export interface ${typeName} {
  id: string
  name?: string | null
  title?: string | null
  slug?: string | null
  label?: string | null
}`
    )
  }

  return Array.from(typeDeclarations.values()).join('\n\n')
}

function relationshipTableVar(field: SchemaField): string {
  return isUserRelationshipTarget(field.relationship) ? 'user' : toCamelCase(field.relationship!)
}

function relationshipSelectShape(field: SchemaField, tableVar: string): string {
  return isUserRelationshipTarget(field.relationship)
    ? `{ id: ${tableVar}.id, name: ${tableVar}.name, email: ${tableVar}.email }`
    : `{ id: ${tableVar}.id, name: ${tableVar}.name, title: ${tableVar}.title, slug: ${tableVar}.slug, label: ${tableVar}.label }`
}

function relationshipRowType(field: SchemaField): string {
  return isUserRelationshipTarget(field.relationship)
    ? '{ id?: string | null; name?: string | null; email?: string | null }'
    : '{ id?: string | null; name?: string | null; title?: string | null; slug?: string | null; label?: string | null }'
}

function relationshipMappingExpression(field: SchemaField, valueExpression: string): string {
  return isUserRelationshipTarget(field.relationship)
    ? `${valueExpression}?.id ? { id: ${valueExpression}.id, name: ${valueExpression}.name ?? undefined, email: ${valueExpression}.email ?? undefined } : null`
    : `${valueExpression}?.id ? { id: ${valueExpression}.id, name: ${valueExpression}.name ?? undefined, title: ${valueExpression}.title ?? undefined, slug: ${valueExpression}.slug ?? undefined, label: ${valueExpression}.label ?? undefined } : null`
}

export function buildRelationshipSelect(
  allDbFields: SchemaField[],
  relationshipFields: SchemaField[],
  tableVar: string,
  htmlFields: SchemaField[] = [],
  extraSelects: string[] = [],
  extraJoins = ''
): string {
  const fieldSelects = allDbFields
    .filter((f) => f.type !== 'relationship')
    .map((f) => `        ${f.name}: ${tableVar}.${f.name}`)
  const htmlSelects = htmlFields.map((f) => `        ${f.name}Html: ${tableVar}.${f.name}Html`)
  const regularSelects = [...fieldSelects, ...htmlSelects].join(',\n')

  const relSelects = relationshipFields
    .map((f) => {
      const relTable = relationshipTableVar(f)
      return `        ${f.name}: ${relationshipSelectShape(f, relTable)}`
    })
    .join(',\n')

  const joins = relationshipFields
    .map((f) => {
      const relTable = relationshipTableVar(f)
      return `.leftJoin(${relTable}, eq(${tableVar}.${f.name}, ${relTable}.id))`
    })
    .join('')

  return `db.select({\n${[regularSelects, relSelects, ...extraSelects].filter(Boolean).join(',\n')}\n      }).from(${tableVar})${joins}${extraJoins}`
}

export function buildResultMapping(
  allDbFields: SchemaField[],
  relationshipFields: SchemaField[],
  Plural: string,
  camelPlural: string,
  Singular: string,
  hasListRels = false
): string {
  const relationshipDeclarations = relationshipFields
    .map(
      (f) => `      const ${f.name} = row.${f.name} as ${relationshipRowType(f)} | null | undefined`
    )
    .join('\n')
  const rowDeclarations = [
    relationshipDeclarations,
    `      const createdByUser = row.createdByUser as { id?: string | null; name?: string | null; email?: string | null } | null | undefined`,
    `      const updatedByUser = row.updatedByUser as { id?: string | null; name?: string | null; email?: string | null } | null | undefined`
  ]
    .filter(Boolean)
    .join('\n')

  const mappedTimestampFields = allDbFields.some((f) => f.name === 'createdAtFormatted')
    ? []
    : [
        `        createdAtFormatted: formatActionDate(row.createdAt)`,
        `        updatedAtFormatted: formatActionDate(row.updatedAt)`
      ]

  const rowMappings = allDbFields
    .map((f) => {
      if (f.type === 'relationship' && !f.multiple) {
        return `        ${f.name}: ${relationshipMappingExpression(f, f.name)}`
      }
      return `        ${f.name}: row.${f.name}`
    })
    .concat([
      ...mappedTimestampFields,
      `        createdByUser: createdByUser?.id ? { id: createdByUser.id, name: createdByUser.name ?? '', email: createdByUser.email ?? '' } : null`,
      `        updatedByUser: updatedByUser?.id ? { id: updatedByUser.id, name: updatedByUser.name ?? '', email: updatedByUser.email ?? '' } : null`
    ])
    .join(',\n')

  const mapBlock = `

    const mapped${Plural} = results.map((rawRow) => {
      const row = rawRow as Record<string, unknown>
${rowDeclarations}

      return {
${rowMappings}
      }
    })`

  if (hasListRels) {
    return `${mapBlock}

    const populated${Plural} = await Promise.all(
      mapped${Plural}.map(record => populate${Singular}ListRelationships(record as ${Singular}))
    )

    return {
      ${camelPlural}: populated${Plural},
      total
    }`
  }

  return `${mapBlock}

    return {
      ${camelPlural}: mapped${Plural} as unknown as ${Singular}[],
      total
    }`
}

export function buildSingleRowMapping(
  allDbFields: SchemaField[],
  _relationshipFields: SchemaField[],
  typeName: string,
  htmlFields: SchemaField[] = []
): string {
  const fieldMappings = allDbFields.map((f) => {
    if (f.type === 'relationship' && !f.multiple) {
      return `      ${f.name}: ${relationshipMappingExpression(f, `row.${f.name}`)}`
    }
    return `      ${f.name}: row.${f.name}`
  })
  const htmlMappings = htmlFields.map((f) => `      ${f.name}Html: row.${f.name}Html`)
  const singleTimestampFields = allDbFields.some((f) => f.name === 'createdAtFormatted')
    ? []
    : [
        `      createdAtFormatted: formatActionDate(row.createdAt)`,
        `      updatedAtFormatted: formatActionDate(row.updatedAt)`
      ]
  const mappings = [
    ...fieldMappings,
    ...htmlMappings,
    ...singleTimestampFields,
    `      createdByUser: row.createdByUser?.id ? { id: row.createdByUser.id, name: row.createdByUser.name, email: row.createdByUser.email } : null`,
    `      updatedByUser: row.updatedByUser?.id ? { id: row.updatedByUser.id, name: row.updatedByUser.name, email: row.updatedByUser.email } : null`
  ].join(',\n')

  return `{\n${mappings}\n    } as unknown as ${typeName}`
}

export function buildExplicitSelect(
  fields: SchemaField[],
  tableVar: string,
  htmlFields: SchemaField[] = [],
  extraSelects: string[] = [],
  extraJoins = ''
): string {
  const fieldSelects = fields.map((f) => `      ${f.name}: ${tableVar}.${f.name}`)
  const htmlSelects = htmlFields.map((f) => `      ${f.name}Html: ${tableVar}.${f.name}Html`)
  const selects = [...fieldSelects, ...htmlSelects, ...extraSelects].join(',\n')
  return `db.select({\n${selects}\n    }).from(${tableVar})${extraJoins}`
}

// ============================================================================
// List-relationship helpers
// ============================================================================

export function findListFieldsWithRelationships(
  fields: SchemaField[]
): Array<{ field: SchemaField; path: string[] }> {
  const result: Array<{ field: SchemaField; path: string[] }> = []
  for (const field of fields) {
    if (field.type === 'list' && field.fields) {
      const hasRels = field.fields.some((f) => f.type === 'relationship' && f.relationship)
      if (hasRels) {
        result.push({ field, path: [field.name] })
      }
    }
  }
  return result
}

export function generatePopulateListRelsFunction(
  queries: Array<{
    fieldPath: string
    relField: SchemaField
    relTable: string
    listFieldName: string
  }>,
  Singular: string
): string {
  const blocks = queries.map((q) => {
    const v = `${q.fieldPath}_${q.relField.name}`
    return `  // ${q.listFieldName} -> ${q.relField.name}
  const ${v}Ids = record.${q.listFieldName} && Array.isArray(record.${q.listFieldName})
    ? (record.${q.listFieldName} as Record<string, unknown>[])
        .map((item) => item.${q.relField.name})
        .filter((id) => id != null && id !== '')
        .map((id) => String(id))
    : []

  const ${v}Results = ${v}Ids.length > 0
    ? await db.select().from(${q.relTable}).where(inArray(${q.relTable}.id, ${v}Ids))
    : []

  const ${v}Map = new Map(${v}Results.map((r) => [r.id, r]))

  if (record.${q.listFieldName} && Array.isArray(record.${q.listFieldName})) {
    record.${q.listFieldName} = (record.${q.listFieldName} as Record<string, unknown>[]).map((item) => {
      const relId = item.${q.relField.name} ? String(item.${q.relField.name}) : null
      return {
        ...item,
        ${q.relField.name}: relId ? ${v}Map.get(relId) || null : null
      }
    })
  }`
  })

  return `
/**
 * Populate relationship fields inside list (JSONB) fields
 */
async function populate${Singular}ListRelationships(record: ${Singular}): Promise<${Singular}> {
${blocks.join('\n\n')}

  return record
}
`
}
